import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios'
import { v4 as uuid } from 'uuid'
import { get, post, put } from '../base'
import type { Character } from '@/types'
import type { ResponseWrapper, SearchQuery, SearchResult } from '@/types/common'
import type {
  CreateUniverseDraftPayload,
  CreateUniversePayload,
  CreatedEventModel,
  GenerateBasicInfoData,
  GenerateUniversePayload,
  GenerateUniverseResult,
  SimpleUniverseResponse,
  UpdateUniverseDraftPayload,
} from '@/types/universe'
import type { AudioModel, IBGMItem, IMedia } from '@/types/audio'
import type { StreamMessageModel } from '@/types/message'

// V2 Char response
export interface CharResponse {
  charId: number
  charName: string
  charDescription: string
  charAvatarUrl: string
  creatorUserId: number
  tags: string[]
  followerCount: number
  useAvatarImage: boolean
}
interface ReturnedMessage {
  charOrUserId: number
  role: string
  name: string
  message: string
  action: string
  messageTimestampUtc: number
}

export interface ReturnedUniverse {
  whatIf: string
  universeId: number
  sekaiId?: number
  background: string
  storyTellerStyle: string
  createTimeUtc: number
  creatorUserId: number
  creatorUserName: string
  charIds: number[]
  chars: Character[]
  userChar: Character
  userCharId: number
  events?: CreatedEventModel[]
  messages?: ReturnedMessage[]
  initializedChatRecords?: string
  bgm?: AudioModel
  isFavorite?: boolean
  firstEventDescription?: string
  highlight: {
    text: string
    width: number
    height: number
    x: number
    y: number
    fontSize: number
  }
  initialRecords?: StreamMessageModel[]
  backgroundImage?: string
}

interface UniverseWithCreatorInfo extends ReturnedUniverse {
  creatorName: string
  creatorAvatarUrl: string
}

type CreateUniverseResult = ReturnedUniverse

export interface UserCharResponse {
  charId: string
  charName: string
  charDescription: string
  charAvatarUrl: string
}

export interface CharacterVoiceItem {
  voiceId: number
  displayName: string
  sampleUrl: string
  displayEnable: boolean
  charName: string
  labsTag: string[]
  labsId: string
  rvcTag: string[]
  rvcId: number
  creatorUserId: number
  creationType: number
  enable: boolean
  createTime: number
  duration: number // second
}

export type GetBGMListResult = Record<string, IBGMItem[]>

export interface PreviousUniverse {
  items: {
    universeId: number
    creatorUserId: number
    gifUrl: string
    creatorUserName: string
    viewCount: number
    highlightText: string
  }[]
}

class UniverseService {
  async search({
    query,
    pageNum,
    pageSize,
  }: SearchQuery): Promise<SearchResult<SimpleUniverseResponse>> {
    const res = await get<SearchResult<SimpleUniverseResponse>>(
      `/universe/search?pageNum=${pageNum}&pageSize=${pageSize}&query=${query}`,
    )
    return res
  }

  async getUniverse(universeId: number) {
    return get<UniverseWithCreatorInfo>(
      `/universe/get?universeId=${universeId}`,
    )
  }

  async getUniverseById(universeId: string | number) {
    return get<ReturnedUniverse>(
      `/universe/v2/getUniverseInfoById?universeId=${universeId}`,
      {
        timeout: 20 * 1000,
      },
    )
  }

  async getSekaiInfoById(id: string | number) {
    return get<ResponseWrapper<ReturnedUniverse>>(
      `/sekai/getSekaiInfo?id=${id}`,
      {
        timeout: 20 * 1000,
      },
    )
  }

  async getCreatedUniverse() {
    return get<ReturnedUniverse[]>('/user/getCreatedUniversesV2')
  }

  async createUniverse(payload: CreateUniversePayload) {
    return post<CreateUniverseResult>('/universe/v2/createUniverse', payload)
  }

  async createUniverseDraft(payload: CreateUniverseDraftPayload) {
    return post<ReturnedUniverse>('/universe/v2/createUniverseDraft', payload)
  }

  async generateUniverse(
    payload: GenerateUniversePayload,
    config?: AxiosRequestConfig<any>,
  ) {
    return post<GenerateUniverseResult>(
      '/universe/v2/generateUniverse',
      payload,
      config,
    )
  }

  async setUniverseAvailable(payload: { universeId: number }) {
    return post<{ message: string }>('/universe/setUniverseAvailable', payload)
  }

  async enableUniverse(payload: { universeId: number }) {
    return post('/universe/v2/updateUniverseEnable', {
      ...payload,
      enable: true,
    })
  }

  async getBGMList() {
    return get<GetBGMListResult>('/bgm/getAllBgm')
  }

  async getVoiceList(voiceId: number) {
    return get<CharacterVoiceItem[]>(
      `/voice/getDisplayVoice?charVoiceId=${voiceId}`,
    )
  }

  async generateBGMPresignedUrl(fileName: string) {
    return post<{
      presignedUrl: string
      s3Prefix: string
    }>('/s3/generatePresignedUrl', {
      fileName: `aiu-bgm/${uuid()}-${fileName}`,
      method: 'put_object',
    })
  }

  async generateVideoPresignedUrl(fileName: string) {
    return post<{
      presignedUrl: string
      s3Prefix: string
    }>('/s3/generatePresignedUrl', {
      fileName: `aiu-media/aiu-video/${uuid()}-${fileName}`,
      method: 'put_object',
    })
  }

  async uploadBGM(
    presignedUrl: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ) {
    return put<{
      presignedUrl: string
    }>(presignedUrl, file, {
      onUploadProgress,
      ignoreAuth: true,
      timeout: 5 * 60 * 1000,
    })
  }

  async createBGM(bgmData: {
    url: string
    name: string
    coverUrl?: string
    duration: number
    tags: string[]
    public: boolean
  }) {
    return post<IBGMItem>('/bgm/createBgm', bgmData, {
      timeout: 60 * 1000,
    })
  }

  async createVideo(videoData: {
    url: string
    name: string
    coverUrl?: string
    duration?: number
    tags?: string[]
    public?: boolean
  }) {
    return post<IMedia>('/video/createVideo', videoData)
  }

  async fetchEventById(eventId: number) {
    return get<CreatedEventModel>(
      `/universe/v2/getEventInfoById?eventId=${eventId}`,
    )
  }

  async generateBasicSetting(whatIf: string) {
    return post<GenerateBasicInfoData>('/universe/v2/generateBasicSetting', {
      whatIf,
    })
  }

  async getMostRecentUniverseDraft() {
    return get<ReturnedUniverse>('/universe/v2/getUserLatestDraft')
  }

  async getUniverseDraftById(draftId: number) {
    return get<ReturnedUniverse>(
      `/universe/v2/getDraftInfoById?draftId=${draftId}`,
    )
  }

  async updateUniverseDraftById(
    draftId: number,
    payload: UpdateUniverseDraftPayload,
  ) {
    return put<ReturnedUniverse>('/universe/v2/updateDraftInfoById', {
      universeId: draftId,
      updateInfo: payload,
    })
  }

  async publishUniverse(draftUniverseId: number) {
    return post<ReturnedUniverse>('universe/v2/publishUniverse', {
      universeId: draftUniverseId,
    })
  }

  async replayPreviewSekai(universeId: number) {
    return post<{ message: string }>('/universe/v2/draftReplay', {
      universeId,
    })
  }

  async getPrepareWhatif() {
    return get<{ data: string }>('/universe/v2/getPreparedWhatIf')
  }

  async getPreviousUniverse(universeId: number) {
    return get<{ data: PreviousUniverse }>(
      `/universe/v2/getPreviousUniverse?universeId=${universeId}`,
    )
  }

  async getNextUniverse(universeId: number) {
    return get<{ data: PreviousUniverse }>(
      `/universe/v2/getNextUniverse?universeId=${universeId}`,
    )
  }
}

export const universeService = new UniverseService()
